import { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Truck,
  Sparkles,
  Megaphone,
  Send,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  History,
} from "lucide-react";

import { API, authHeaders } from "./admin/shared";
import AdminLogin from "./admin/AdminLogin";
import AnalyticsTab from "./admin/AnalyticsTab";
import IssuesTab from "./admin/IssuesTab";
import MarketingTab from "./admin/MarketingTab";
import PromotionsTab from "./admin/PromotionsTab";
import EmailListTab from "./admin/EmailListTab";
import ServicesTab from "./admin/ServicesTab";
import GalleryTab from "./admin/GalleryTab";
import BeforeAfterTab from "./admin/BeforeAfterTab";
import ReviewsTab from "./admin/ReviewsTab";
import BookingsTab from "./admin/BookingsTab";
import CalendarTab from "./admin/CalendarTab";
import StyleRequestsTab from "./admin/StyleRequestsTab";
import MessagesTab from "./admin/MessagesTab";
import RebookingTab from "./admin/RebookingTab";
import SuppliersTab from "./admin/SuppliersTab";
import GoLiveTab from "./admin/GoLiveTab";
import BlockedMessagesTab from "./admin/BlockedMessagesTab";
import ConsentTab from "./admin/ConsentTab";
import CampaignsTab from "./admin/CampaignsTab";
import SafeModeBanner from "./admin/SafeModeBanner";

const AdminDashboard = () => {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [issuesCount, setIssuesCount] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, authHeaders());
        setAdmin(res.data);
      } catch (err) {
        if (err.response?.status !== 401) console.error("Auth check failed:", err);
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
  }, []);

  // Poll the issues count so the sidebar badge stays fresh
  useEffect(() => {
    if (!admin) return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/admin/failed-attempts`, authHeaders());
        if (alive) setIssuesCount(res.data.counts?.total || 0);
      } catch (e) { console.error("Failed to fetch issues count:", e); }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [admin]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authHeaders());
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    localStorage.removeItem("admin_token");
    setAdmin(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <p className="font-sans text-sm text-brand-text-muted">Loading...</p>
      </div>
    );
  }

  if (!admin) return <AdminLogin onLogin={setAdmin} />;

  const sidebarItems = [
    { value: "analytics", label: "Dashboard", icon: LayoutDashboard },
    { value: "issues", label: "Issues", icon: AlertTriangle, badge: "issues" },
    { value: "rebooking", label: "Rebooking", icon: RotateCcw },
    { value: "marketing", label: "Marketing", icon: Megaphone },
    { value: "promotions", label: "Promotions", icon: Send },
    { value: "emaillist", label: "Email List", icon: Mail },
    { value: "services", label: "Services", icon: Scissors },
    { value: "gallery", label: "Gallery", icon: Image },
    { value: "beforeafter", label: "Before/After", icon: Sparkles },
    { value: "reviews", label: "Reviews", icon: Star },
    { value: "bookings", label: "Bookings", icon: CalendarDays },
    { value: "calendar", label: "Calendar / Walk-Ins", icon: CalendarPlus },
    { value: "requests", label: "Style Requests", icon: MessageSquare },
    { value: "messages", label: "Messages", icon: MessageSquare },
    { value: "suppliers", label: "Suppliers", icon: Truck },
    { value: "golive", label: "Go-Live", icon: Rocket },
    { value: "blockedlog", label: "Blocked Log", icon: ShieldAlert },
    { value: "consent", label: "SMS Consent", icon: ShieldCheck },
    { value: "campaigns", label: "Campaigns", icon: History },
  ];

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="border-b border-brand-border bg-brand-surface">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-serif text-xl text-brand-text" data-testid="admin-home-link">
              Afro <span className="text-brand-gold">Magic</span>
            </a>
            <span className="text-xs font-sans text-brand-text-muted bg-brand-gold/10 text-brand-gold px-2 py-0.5 border border-brand-gold/20">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/walkin" className="font-sans text-xs text-brand-gold border border-brand-gold px-3 py-1 hover:bg-brand-gold hover:text-black transition-colors" data-testid="admin-walkin-link">Walk-In Form</a>
            <a href="/business-card" className="font-sans text-xs text-brand-text-muted border border-brand-border px-3 py-1 hover:text-brand-gold hover:border-brand-gold transition-colors" data-testid="admin-card-link">QR & Cards</a>
            <a href="/flyers" className="font-sans text-xs text-brand-text-muted border border-brand-border px-3 py-1 hover:text-brand-gold hover:border-brand-gold transition-colors" data-testid="admin-flyers-link">Flyers</a>
            <span className="font-sans text-xs text-brand-text-muted">{admin.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 font-sans text-xs text-brand-text-muted hover:text-red-400 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <SafeModeBanner />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="analytics" className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <TabsList className="flex md:flex-col h-auto bg-transparent p-0 gap-1 md:w-48 flex-shrink-0" data-testid="admin-sidebar">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const showBadge = item.badge === "issues" && issuesCount > 0;
              return (
                <TabsTrigger
                  key={item.value}
                  value={item.value}
                  data-testid={`admin-tab-${item.value}`}
                  className="justify-start gap-2 px-4 py-2.5 font-sans text-sm text-brand-text-muted rounded-none border border-transparent data-[state=active]:bg-brand-surface data-[state=active]:border-brand-border data-[state=active]:text-brand-gold w-full"
                >
                  <Icon size={16} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {showBadge && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full" data-testid="issues-badge">
                      {issuesCount > 99 ? "99+" : issuesCount}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
            <TabsContent value="issues"><IssuesTab onCountChange={setIssuesCount} /></TabsContent>
            <TabsContent value="rebooking"><RebookingTab /></TabsContent>
            <TabsContent value="marketing"><MarketingTab /></TabsContent>
            <TabsContent value="promotions"><PromotionsTab /></TabsContent>
            <TabsContent value="emaillist"><EmailListTab /></TabsContent>
            <TabsContent value="services"><ServicesTab /></TabsContent>
            <TabsContent value="gallery"><GalleryTab /></TabsContent>
            <TabsContent value="beforeafter"><BeforeAfterTab /></TabsContent>
            <TabsContent value="reviews"><ReviewsTab /></TabsContent>
            <TabsContent value="bookings"><BookingsTab /></TabsContent>
            <TabsContent value="calendar"><CalendarTab /></TabsContent>
            <TabsContent value="requests"><StyleRequestsTab /></TabsContent>
            <TabsContent value="messages"><MessagesTab /></TabsContent>
            <TabsContent value="suppliers"><SuppliersTab /></TabsContent>
            <TabsContent value="golive"><GoLiveTab /></TabsContent>
            <TabsContent value="blockedlog"><BlockedMessagesTab /></TabsContent>
            <TabsContent value="consent"><ConsentTab /></TabsContent>
            <TabsContent value="campaigns"><CampaignsTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
